import { useContext, useEffect, useReducer, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Pencil } from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { Skeleton } from 'components/ui/skeleton';
import { useToast } from 'components/ui/toast';
import { ConfirmDeleteDialog } from 'components/ConfirmDeleteDialog';
import AppContext from 'contexts/AppContext';
import { hasUserPermission, PERMISSIONS } from 'utils/permissionUtils';
import { apiErrorMessage } from 'utils/errorMessage';
import { usePageCrumb } from 'components/admin/AppTopbar';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from 'i18n';
import { useTechnician, useCreateTechnician, useUpdateTechnician } from 'hooks/useTechnicians';
import type { Technician } from 'api/techniciansApi';
import { provisioningNotice } from 'utils/jiraProvisioning';
import { TECHNICIAN_DEFAULT_RIGHTS, type UserRights } from 'api/usersApi';
import { controlId, FIELD_ORDER, formErrors, type FormErrors } from './validation';
import ReferentialPageHeader from '../AdminReferentials/components/ReferentialPageHeader';
import GeneralSection, { type GeneralValues } from './GeneralSection';
import ScopeSection from './ScopeSection';
import SkillsSection from './SkillsSection';
import ActivitySection from './ActivitySection';
import RatingsSection from './RatingsSection';
import UnavailabilitySection from './UnavailabilitySection';
import RightsSection from './RightsSection';

interface FormState extends GeneralValues {
  isActive: boolean;
  countryCode: string;
  geoAreaIds: number[];
  specialtyIds: number[];
  expertiseIds: number[];
}

type Action = { type: 'load'; technician: Technician } | { type: 'patch'; patch: Partial<FormState> };

/**
 * The identifier the person signs in with, derived: given name as typed, family name in capitals
 * (the French administrative convention). Capped at the 120 the `name` schema allows, so a long
 * pair degrades instead of coming back as a 400.
 */
const derivedUsername = (firstName: string, lastName: string): string =>
  [firstName.trim(), lastName.trim().toUpperCase()].filter(Boolean).join(' ').slice(0, 120);

// The language the country proposes. Only a proposal: a manual pick is never overwritten.
const COUNTRY_LANG: Record<string, string> = { FR: 'fr', ES: 'es', GB: 'en' };

// The header's action pair is outside the <form>, and `<button form="…">` is what reconnects it.
const FORM_ID = 'technician-record-form';

// A record written before the enum, or with a regional tag, must still show a language.
const resolveLang = (lang: string): string =>
  (SUPPORTED_LOCALES as readonly string[]).includes(lang) ? lang : DEFAULT_LOCALE;

const EMPTY: FormState = {
  username: '',
  email: '',
  firstName: '',
  lastName: '',
  company: '',
  phone: '',
  password: '',
  lang: DEFAULT_LOCALE,
  isActive: true, // creation default, and the value the design shows on the create form
  countryCode: '',
  geoAreaIds: [],
  specialtyIds: [],
  expertiseIds: [],
};

// One reducer for the whole record: the sections patch it, nothing owns a slice of its own, so
// the country change that clears the areas is a single atomic update rather than two renders.
const reducer = (state: FormState, action: Action): FormState => {
  switch (action.type) {
    case 'load':
      return {
        ...EMPTY,
        email: action.technician.email,
        firstName: action.technician.profile.firstName ?? '',
        lastName: action.technician.profile.lastName ?? '',
        company: action.technician.profile.company ?? '',
        phone: action.technician.profile.phone ?? '',
        // The identifier as stored, not as derived: an admin who only fixes a phone number must
        // not rename the login. The derivation takes over as soon as a name is actually typed.
        username: action.technician.username,
        lang: resolveLang(action.technician.lang),
        isActive: action.technician.isActive,
        countryCode: action.technician.profile.countryCode ?? '',
        geoAreaIds: action.technician.geoAreaIds,
        specialtyIds: action.technician.specialtyIds,
        expertiseIds: action.technician.expertiseIds,
      };
    case 'patch': {
      const touchesName = 'firstName' in action.patch || 'lastName' in action.patch;
      const next = { ...state, ...action.patch };
      // Recomputed on every name keystroke; never emptied, because `username` is a required `name`.
      if (!touchesName) return next;
      return { ...next, username: derivedUsername(next.firstName, next.lastName) || state.username };
    }
    default:
      return state;
  }
};

const AdminTechnician = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  /**
   * Now that the directory is the way in, the record needs a way out. `navigate(-1)` keeps the
   * list's filters, which live in its query string — but a deep link has no in-app history to go
   * back to, and react-router marks that first entry `key: 'default'`.
   */
  const backToList = () =>
    location.key === 'default' ? navigate('/admin/technicians') : navigate(-1);
  const { currentUser } = useContext(AppContext);

  // A single `:id` route serves both screens, so `new` arrives here as the id. Anything else that
  // is not a plain number stays null rather than becoming `NaN`, which would be fetched as
  // `/admin/technicians/NaN` and answered 400.
  const creating = id === 'new';
  const technicianId = creating || !/^\d+$/.test(id ?? '') ? null : Number(id);
  const { data: technician, isLoading, error } = useTechnician(technicianId);
  const createTechnician = useCreateTechnician();
  const updateTechnician = useUpdateTechnician();
  const saving = createTechnician.isPending || updateTechnician.isPending;

  const [form, dispatch] = useReducer(reducer, EMPTY);
  /**
   * Creation only, and deliberately outside the record's reducer: the rights live on `users`, not
   * on the business record, and an existing one has the card write them on `/users/:id` instead.
   */
  const [creationRights, setCreationRights] = useState<UserRights>(TECHNICIAN_DEFAULT_RIGHTS);
  const [confirmingDeactivation, setConfirmingDeactivation] = useState(false);
  // Nothing is flagged before the first submit — shouting at a field nobody has reached yet is how
  // a form loses a reader. From then on the map recomputes, so fixing a field clears its message.
  const [submitted, setSubmitted] = useState(false);

  /**
   * The mode is local state, not `?edit=`: an URL parameter would make "Annuler" a history entry,
   * so a browser Back would reopen the draft that was just abandoned. The list's pencil promises
   * an edit, and says so through a location state — which pollutes neither history nor a link.
   */
  const editIntent = (location.state as { edit?: boolean } | null)?.edit === true;
  const [editing, setEditing] = useState(creating || editIntent);
  const [leaving, setLeaving] = useState<'read' | 'list' | null>(null);
  const loadedId = useRef<number | null>(null);
  const langPicked = useRef(false);

  useEffect(() => {
    // Once per record, not on every refetch: a background refetch must not wipe what is being typed.
    if (technician && loadedId.current !== technician.id) {
      loadedId.current = technician.id;
      dispatch({ type: 'load', technician });
    }
  }, [technician]);

  useEffect(() => {
    // The pencil's intent is spent on arrival: history state survives a reload, which must come back in read mode.
    if (editIntent) navigate(location.pathname, { replace: true, state: null });
  }, [editIntent, location.pathname, navigate]);

  // A viewer without the manage permission reads the record; it renders disabled fields rather
  // than buttons that would 403. `canEdit` is the server's own verdict on this row.
  const canManage =
    hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_TECHNICIANS_MANAGE) &&
    !(!creating && technician?.canEdit === false);
  // Not a mode of its own: a read-only account is simply never allowed to leave read mode.
  const readOnly = !canManage || !editing;

  useEffect(() => {
    // The permission can go while the form is open: collapse the pair rather than offer a save
    // that the disabled fields have already made pointless.
    if (!canManage && editing) setEditing(false);
  }, [canManage, editing]);

  /**
   * The country pre-fills the language, and only while nobody has picked one: the pre-fill is a
   * proposal, so changing country afterwards must not undo a deliberate choice.
   */
  const patch = (values: Partial<FormState>) => {
    if (values.lang !== undefined) langPicked.current = true;
    const proposed = values.countryCode ? COUNTRY_LANG[values.countryCode] : undefined;
    const withLang = proposed && !langPicked.current ? { ...values, lang: proposed } : values;
    dispatch({ type: 'patch', patch: withLang });
  };

  // The PUT body is the whole record, so a payload is built from a state rather than patched.
  const toPayload = (state: FormState) => ({
    username: state.username,
    email: state.email,
    firstName: state.firstName || null,
    lastName: state.lastName || null,
    company: state.company,
    phone: state.phone,
    countryCode: state.countryCode || null,
    lang: state.lang,
    geoAreaIds: state.geoAreaIds,
    specialtyIds: state.specialtyIds,
    expertiseIds: state.expertiseIds,
  });

  const payload = toPayload(form);

  const submit = () => {
    if (saving) return;
    if (creating) {
      createTechnician.mutate(
        { ...payload, password: form.password, ...creationRights },
        {
          onSuccess: ({ technician: created, jira }) => {
            // One toast, not two: the account exists, and the Jira half is a caveat on the same
            // sentence rather than a second event.
            const notice = provisioningNotice(t, created.username, jira);
            toast[notice.tone](notice.message);
            setEditing(false); // the same component serves the new id: it would stay in edit
            setSubmitted(false);
            navigate(`/admin/technicians/${created.id}`, { replace: true });
          },
          onError: (mutationError) => toast.error(apiErrorMessage(t, mutationError)),
        }
      );
      return;
    }
    updateTechnician.mutate(
      { id: technicianId as number, data: { ...payload, isActive: form.isActive } },
      {
        onSuccess: () => {
          // Re-arm the load guard: the id has not changed, so without this the refetch is
          // ignored and read mode keeps painting the pre-save form instead of the server's.
          loadedId.current = null;
          setEditing(false);
          setSubmitted(false);
          toast.success(t('Record saved'));
        },
        onError: (mutationError) => {
          // The switch states the server's truth, so a refused save puts it back rather than
          // leaving the screen claiming a deactivation that never happened. Typed text is kept.
          if (technician) patch({ isActive: technician.isActive });
          toast.error(apiErrorMessage(t, mutationError));
        },
      }
    );
  };

  // Recomputed on every render once a save was attempted, so a corrected field drops its message.
  const errors: FormErrors = submitted ? formErrors(form, creating) : {};

  /**
   * The status is a form field now, so the switch no longer sends anything on its own — but losing
   * the deactivation confirmation with the button would have been a silent regression: it moves
   * onto Save, and fires only on the active → inactive step.
   *
   * Nothing leaves before the rules pass, and the first offending control takes focus: six cards
   * deep, a message the page never scrolls to is a message nobody reads.
   */
  const save = (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    setSubmitted(true);
    const found = formErrors(form, creating);
    const firstInvalid = FIELD_ORDER.filter((field) => found[field])[0];
    if (firstInvalid) {
      document.getElementById(controlId(firstInvalid))?.focus();
      return;
    }
    if (!creating && technician?.isActive && !form.isActive) return setConfirmingDeactivation(true);
    return submit();
  };

  // The URL only carries the id; the crumb takes the name — or nothing at all while it loads,
  // which leaves the raw segment in place rather than flashing an empty crumb.
  const fullName = [technician?.profile.firstName, technician?.profile.lastName].filter(Boolean).join(' ');
  usePageCrumb(creating ? t('New technician') : fullName || technician?.username || null);

  const stored = technician ? reducer(EMPTY, { type: 'load', technician }) : EMPTY;
  const dirty = JSON.stringify(form) !== JSON.stringify(stored);

  const toRead = () => {
    dispatch({ type: 'load', technician: technician as Technician }); // discard: back to what is stored
    setEditing(false);
    setSubmitted(false);
  };

  // Leaving an edit with unsaved input confirms first. The browser's own Back is deliberately not
  // intercepted: a react-router blocker for one screen costs more than it protects, and a refresh
  // comes back in read mode anyway.
  const exit = (target: 'read' | 'list') => {
    if (editing && dirty) return setLeaving(target);
    return target === 'list' ? backToList() : toRead();
  };

  if (!creating && isLoading) {
    return (
      <div className="container mx-auto space-y-4 p-6">
        {[0, 1, 2, 3].map((row) => (
          <Skeleton key={row} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  if (!creating && (error || !technician)) {
    return (
      <div className="container mx-auto space-y-4 p-6">
        <p className="text-sm text-red-600">
          {error ? apiErrorMessage(t, error) : t('This technician does not exist')}
        </p>
        <Button variant="outline" onClick={() => navigate('/admin/technicians')}>
          {t('Back to the technicians')}
        </Button>
      </div>
    );
  }

  // The design draws this pair twice: header and footer. Six cards deep, going back up to find
  // "Enregistrer" is the flaw it corrected itself. The header's pair sits outside the <form>, so
  // both submit through the `form` attribute — one handler, and Entrée saves from any field.
  const editActions = (
    <>
      <Button type="button" variant="outline" onClick={() => exit(creating ? 'list' : 'read')} disabled={saving}>
        {t('Cancel')}
      </Button>
      <Button type="submit" form={FORM_ID} disabled={saving}>
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
        {creating ? t('Create the technician') : t('Save the record')}
      </Button>
    </>
  );

  return (
    <div className="container mx-auto space-y-4 p-6">
      <ReferentialPageHeader
        title={creating ? t('New technician') : fullName || form.username || t('Technician')}
        subtitle={t('Identity, scope, skills and activity of a field technician')}
        leadingAction={
          <button
            type="button"
            onClick={() => exit('list')}
            aria-label={t('Back')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] border border-zinc-200 bg-white text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-50"
          >
            <ArrowLeft className="h-[18px] w-[18px]" aria-hidden="true" />
          </button>
        }
        titleBadges={
          <>
            <span className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 dark:text-zinc-300">
              <span
                className={cn('h-2 w-2 rounded-full', form.isActive ? 'bg-emerald-500' : 'bg-zinc-400')}
                aria-hidden="true"
              />
              {form.isActive ? t('Active') : t('Inactive')}
            </span>
            {editing && (
              <span className="inline-flex h-[23px] items-center rounded-full bg-amber-50 px-2 text-xs font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-400">
                {creating ? t('New record') : t('Edit mode')}
              </span>
            )}
          </>
        }
        secondaryAction={
          editing ? (
            editActions
          ) : canManage ? (
            <Button onClick={() => setEditing(true)}>
              <Pencil className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {t('Edit')}
            </Button>
          ) : null
        }
      />

      {/* A real form at last, so Entrée saves and the required/type="email" attributes describe a
          control that is genuinely part of one. `noValidate`: the browser's own bubble is one
          message at a time, in the browser's language, and it cannot say "72 octets". */}
      <form id={FORM_ID} noValidate onSubmit={save} className="space-y-4">
        <GeneralSection
          values={form}
          onChange={patch}
          readOnly={readOnly}
          creating={creating}
          isActive={form.isActive}
          onToggleActive={(isActive) => patch({ isActive })}
          externalId={technician?.externalId ?? null}
          storedUsername={technician?.username ?? null}
          errors={errors}
        />

        <ScopeSection
          countryCode={form.countryCode}
          geoAreaIds={form.geoAreaIds}
          onChange={patch}
          readOnly={readOnly}
          creating={creating}
          errors={errors}
        />

        <SkillsSection
          countryCode={form.countryCode}
          specialtyIds={form.specialtyIds}
          expertiseIds={form.expertiseIds}
          onChange={patch}
          readOnly={readOnly}
          creating={creating}
          errors={errors}
        />
      </form>

      {/* Outside the <form>: on an existing record the rights are a partial PUT on `/users/:id`
          with its own permission and its own save. On a creation the same card edits the draft
          the create call carries — the pickers the users form used to offer. */}
      {creating ? (
        <RightsSection
          userId={null}
          value={creationRights}
          onChange={(values) => setCreationRights((current) => ({ ...current, ...values }))}
        />
      ) : (
        technicianId !== null && <RightsSection userId={technicianId} />
      )}

      {/* Read-only, without exception: the technician is the sole author of his absences (D4).
          Absent while creating — a record that does not exist yet cannot be said to be available,
          and there is no id to read periods for. */}
      {!creating && technician && (
        <UnavailabilitySection
          periods={technician.unavailabilities}
          availability={technician.availability}
        />
      )}

      {!creating && technician && <ActivitySection activity={technician.activity} />}

      <RatingsSection avg={technician?.ratings.avg ?? null} count={technician?.ratings.count ?? 0} />

      {/* No delete and no status button, in either state: a technician is deactivated (B-04)
          through the switch above, and the pair the design duplicates is all that is left. */}
      {!readOnly && (
        <div className="flex flex-wrap items-center justify-end gap-2">{editActions}</div>
      )}

      {leaving && (
        <ConfirmDeleteDialog
          open
          tone="warning"
          title={t('Discard your changes?')}
          description={t('The fields you edited are not saved yet. Leaving this mode restores the record as it is stored.')}
          confirmLabel={t('Discard')}
          onClose={() => setLeaving(null)}
          onConfirm={() => {
            const target = leaving;
            setLeaving(null);
            return target === 'list' ? backToList() : toRead();
          }}
        />
      )}

      {confirmingDeactivation && (
        <ConfirmDeleteDialog
          open
          tone="warning"
          title={t('Deactivate {{name}}?', { name: form.username })}
          description={t('Once deactivated, they can no longer sign in to the mobile app, nor receive interventions. Their record and their history are kept, and they can be reactivated at any time.')}
          confirmLabel={t('Deactivate and save')}
          loading={saving}
          onClose={() => {
            // The dialog only opens on a live account, so dismissing it puts the switch back:
            // nothing was sent, and a screen reading "Inactif" would be claiming otherwise.
            setConfirmingDeactivation(false);
            patch({ isActive: true });
          }}
          onConfirm={() => {
            setConfirmingDeactivation(false);
            return submit();
          }}
        />
      )}
    </div>
  );
};

export default AdminTechnician;
